import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import FeeEntryModal from "../components/FeeEntryModal";
import ReceiptDownload from "../components/ReceiptDownload";
import VoucherDownload from "../components/VoucherDownload";
import AnnualFeeInput from "../components/AnnualFeeInput";
import "../styles/FeeAccount.css";
import "../styles/FeeAccountTabs.css";
import API_URL from "../apiConfig";

const YEAR_LABELS = ["1st Year GNM", "2nd Year GNM", "3rd Year GNM"];
const YEAR_VALUES = [1, 2, 3];

function formatCurrency(num) {
  return "₹" + Number(num || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/* ---------- Date helpers (show dd-mm-yyyy in UI/receipts) ---------- */

function parsePaymentDate(s) {
  if (!s) return null;
  if (s instanceof Date) return Number.isFinite(s.getTime()) ? s : null;
  const str = String(s).trim();
  if (!str) return null;

  // ISO yyyy-mm-dd or yyyy-mm-ddTHH...
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [yyyy, mm, dd] = [iso[1], iso[2], iso[3]];
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return Number.isFinite(d.getTime()) ? d : null;
  }

  // dd-mm-yyyy or dd/mm/yyyy
  const dmy = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dmy) {
    const [dd, mm, yyyy] = [dmy[1], dmy[2], dmy[3]];
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return Number.isFinite(d.getTime()) ? d : null;
  }

  // fallback to Date.parse
  const t = Date.parse(str);
  if (!Number.isNaN(t)) return new Date(t);
  return null;
}

function formatDateForDisplay(dateLike) {
  const dt = dateLike instanceof Date ? dateLike : parsePaymentDate(dateLike);
  if (!dt) return "-";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

// convert to YYYY-MM-DD (backend-friendly) or null
function toYMD(dateLike) {
  const dt = dateLike instanceof Date ? dateLike : parsePaymentDate(dateLike);
  if (!dt) return null;
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/* ---------- Carryforward helpers ---------- */

function getNextCFNo(records, year) {
  const cfRecords = records.filter(
    r => Number(r.course_year) === year && String(r.mode || "").toLowerCase().includes("carryforward")
  );
  return `CF${cfRecords.length + 1}`;
}

function getCarryforwardRecords(records, year) {
  return records.filter(
    r => Number(r.course_year) === year && String(r.mode || "").toLowerCase().includes("carryforward")
  );
}

function getCarryforwardAmount(records, year) {
  return records
    .filter(r => Number(r.course_year) === year && String(r.mode || "").toLowerCase().includes("carryforward"))
    .reduce((sum, r) => sum + parseFloat(Number(r.amount || 0)), 0);
}

/*
  findTriggerDateForExcess:
  - sorts records by parsed payment_date (dated first)
  - calculates cumulative received using same rules: tuition_paid + scholarship + carry - refund
  - returns the payment_date in YYYY-MM-DD (for payload) of the first record that causes cumulative > annualFee
*/
function findTriggerDateForExcess(records = [], annualFee = 0) {
  if (!records || records.length === 0) return null;

  const arr = records
    .map(r => ({ r, parsed: parsePaymentDate(r.payment_date || r.paymentDate || r.created_at) }))
    .sort((a, b) => {
      if (a.parsed && b.parsed) return a.parsed - b.parsed;
      if (a.parsed && !b.parsed) return -1;
      if (!a.parsed && b.parsed) return 1;
      return 0;
    });

  let cumulative = 0;
  for (const item of arr) {
    const r = item.r;
    const isCarry = r.mode && String(r.mode).toLowerCase().includes("carryforward");

    const tuitionContribution =
      !isCarry && r.status && String(r.status).toLowerCase() === "paid"
        ? Number(r.amount_paid ?? r.amount ?? 0)
        : 0;

    const scholarshipContribution =
      !(r.status && String(r.status).toLowerCase() === "refunded")
        ? Number(r.scholarship_amount ?? 0)
        : 0;

    const carryContribution = isCarry ? Number(r.amount ?? 0) : 0;

    const refundContribution =
      (r.status && String(r.status).toLowerCase() === "refunded")
        ? Math.abs(Number(r.scholarship_amount ?? 0))
        : 0;

    cumulative += tuitionContribution + scholarshipContribution + carryContribution - refundContribution;

    if (cumulative > (Number(annualFee) || 0)) {
      // return Y-M-D string
      const parsed = item.parsed;
      if (parsed) return toYMD(parsed);
      const normalized = toYMD(r.payment_date ?? r.paymentDate ?? r.created_at);
      if (normalized) return normalized;
      return null;
    }
  }

  return null;
}

/* ---------- Print helper ---------- */
/* Note: this opens a print window only when the user explicitly clicks "Print This Year" or "Print All Years".
   We intentionally keep it because printing HTML requires a new window. This is NOT used by Add Fee flow. */
const handlePrintTable = (ref, summaryData = null) => {
  if (!ref || !ref.current) return;
  const win = window.open("", "_blank");
  if (!win) {
    alert("Unable to open print window — please allow popups for this site.");
    return;
  }
  win.document.write("<html><head><title>Print</title>");
  win.document.write(
    `<style>
      body { font-family: Arial, sans-serif; padding: 22px; color: #222; }
      h2 { text-align:center; }
      .print-summary { font-size: 16px; margin-bottom: 18px; }
      .print-summary span { display: inline-block; margin-right: 16px; }
      table { border-collapse: collapse; width: 100%; font-size: 14px;}
      th, td { border: 1px solid #888; padding: 6px 8px; text-align: center; }
      th { background: #eaf3ff; color: #184e77;}
    </style>`
  );
  win.document.write("</head><body>");
  if (summaryData) {
    win.document.write(`<div class="print-summary">
      <span><b>Class:</b> ${summaryData.classLabel}</span>
      <span><b>Annual Fee:</b> ${summaryData.annualFee}</span>
      <span><b>Total Paid (Tuition):</b> ${summaryData.totalPaid}</span>
      <span><b>Total Scholarship:</b> ${summaryData.totalScholarship}</span>
      <span><b>Total Refund:</b> ${summaryData.totalRefund}</span>
      <span><b>Carryforward:</b> ${summaryData.carryforward}</span>
      <span><b>Total Received:</b> ${summaryData.totalReceived}</span>
      <span><b>Pending Amount:</b> <span style="color: red;">${summaryData.pending}</span></span>
    </div>`);
  }
  win.document.write(ref.current.outerHTML);
  win.document.write("</body></html>");
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 350);
};

/* ---------- Refund detection helper (robust) ---------- */
function isRefundRow(row) {
  if (!row) return false;
  const rn = String(row.receipt_no || row.voucher_no || "").toUpperCase();
  const acc = String(row.account_head || "").toLowerCase();
  const status = String(row.status || row.payment_status || "").toLowerCase();
  const refundField = row.refund ?? row.refund_amount ?? row.refunded_amount ?? 0;
  const amt = Number(row.amount ?? row.amount_paid ?? 0);

  if (acc && acc.includes("refund")) return true;
  if (rn && rn.startsWith("RE")) return true;
  if (status && (status === "refunded" || status.includes("refund"))) return true;
  if (!Number.isNaN(Number(refundField)) && Number(refundField) !== 0) return true;
  if (!Number.isNaN(amt) && amt < 0) return true;
  return false;
}

/* ---------- Component ---------- */

export default function FeeAccountTabs({ token, user }) {
  const { studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [feeRecords, setFeeRecords] = useState([]);
  const [currentYear, setCurrentYear] = useState(1);
  const [showAddFee, setShowAddFee] = useState(false);
  const [editFee, setEditFee] = useState(null);
  const [showEditFee, setShowEditFee] = useState(false);
  const [batchAnnualFee, setBatchAnnualFee] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const printRef = useRef();
  const printAllRef = useRef();

  // normalize role for checks
  const userRole = String(user?.role || "").toLowerCase();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [studentId, currentYear]);

  const fetchData = async () => {
    setError("");
    setLoading(true);
    try {
      const resSummary = await fetch(`${API_URL}/api/fees/summary/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resSummary.ok) {
        setError(`Failed to load student summary (status ${resSummary.status})`);
        setStudent(null);
        setFeeRecords([]);
        setLoading(false);
        return;
      }
      const studentData = await resSummary.json();
      setStudent(studentData);
      setBatchId(studentData.batch_id || null);
      setBatchAnnualFee(
        studentData.annual_fee !== undefined && studentData.annual_fee !== null
          ? parseFloat(Number(studentData.annual_fee))
          : null
      );

      const resRecords = await fetch(`${API_URL}/api/fees/records/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resRecords.ok) {
        setError(`Failed to load fee records (status ${resRecords.status})`);
        setFeeRecords([]);
        setLoading(false);
        return;
      }
      setFeeRecords(await resRecords.json());
    } catch (err) {
      setError("Unable to load fee details. Please check backend server and API routes.");
      setStudent(null);
      setFeeRecords([]);
    }
    setLoading(false);
  };

  const refreshData = () => fetchData();

  function getRecordsForYear(y) {
    return feeRecords.filter(rec => Number(rec.course_year) === Number(y));
  }

  const recordsForYear = getRecordsForYear(currentYear);
  const carryforwardAmount = getCarryforwardAmount(feeRecords, currentYear);

  const totalPaid = recordsForYear.reduce(
    (sum, r) => r.mode !== "Carryforward from previous year" && r.status === "paid"
      ? sum + parseFloat(Number(r.amount_paid || r.amount || 0))
      : sum,
    0
  );
  const totalScholarship = recordsForYear.reduce((sum, r) =>
    r.status && r.status.toLowerCase() === "refunded" ? sum : sum + parseFloat(Number(r.scholarship_amount || 0))
  , 0);
  const totalRefund = recordsForYear.reduce((sum, r) =>
    r.status && r.status.toLowerCase() === "refunded"
      ? sum + Math.abs(parseFloat(Number(r.scholarship_amount || 0)))
      : sum
  , 0);

  const totalReceived = totalPaid + totalScholarship + carryforwardAmount - totalRefund;

  const nextYear = currentYear + 1;
  const nextYearCarryforwardRecords = getCarryforwardRecords(feeRecords, nextYear);

  let pending =
    (currentYear === 1 || currentYear === 2) && nextYearCarryforwardRecords.length
      ? 0
      : (batchAnnualFee || 0) - totalReceived;

  const canCarryForward =
    (currentYear === 1 || currentYear === 2) &&
    pending < 0 &&
    Math.abs(pending) > 0 &&
    !nextYearCarryforwardRecords.length;

  const canRefund =
    currentYear === 3 &&
    pending < 0 &&
    Math.abs(pending) > 0;

  // New behavior:
  // Prefer the most-recent payment date among the records that actually contributed positive amount
  // (this matches the user's expectation: when they just entered a new bill with date X, CF should use X).
  const handleCarryForward = async () => {
    try {
      const cfNo = getNextCFNo(feeRecords, nextYear);
      const cfAmount = Math.abs(pending);

      // Candidate records are those that provided positive incoming (amount_paid + scholarship) and are not refunds.
      const candidateRecords = recordsForYear
        .map(r => {
          const isCarry = r.mode && String(r.mode).toLowerCase().includes("carryforward");
          const amtPaid = (!isCarry && r.status && String(r.status).toLowerCase() === "paid") ? Number(r.amount_paid ?? r.amount ?? 0) : 0;
          const scholarship = !(r.status && String(r.status).toLowerCase() === "refunded") ? Number(r.scholarship_amount ?? 0) : 0;
          const incoming = amtPaid + scholarship + (isCarry ? Number(r.amount ?? 0) : 0);
          return { r, incoming, parsed: parsePaymentDate(r.payment_date) };
        })
        .filter(x => x.incoming > 0 && x.parsed); // only those with positive incoming and a parseable date

      // Find the most recent (max) parsed date among candidates
      let cfPaymentYMD = null;
      if (candidateRecords.length) {
        candidateRecords.sort((a, b) => b.parsed - a.parsed);
        cfPaymentYMD = toYMD(candidateRecords[0].parsed); // Y-M-D for payload
      }

      // Fallback: if no candidateRecords found, fallback to the original trigger logic (first record that caused excess)
      if (!cfPaymentYMD) {
        const triggerYMD = findTriggerDateForExcess(recordsForYear, batchAnnualFee);
        if (triggerYMD) cfPaymentYMD = triggerYMD;
      }

      // Last fallback: pick latest payment_date string (if any) or today
      if (!cfPaymentYMD) {
        const parsedDates = recordsForYear
          .map(r => parsePaymentDate(r.payment_date))
          .filter(Boolean)
          .map(d => toYMD(d))
          .filter(Boolean)
          .sort()
          .reverse();
        cfPaymentYMD = parsedDates.length ? parsedDates[0] : toYMD(new Date());
      }

      const cfPayload = {
        student_id: studentId,
        course_year: nextYear,
        amount: parseFloat(cfAmount.toFixed(2)),
        scholarship_amount: 0,
        status: "paid",
        mode: "Carryforward from previous year",
        payment_date: cfPaymentYMD, // YYYY-MM-DD
        receipt_no: cfNo,
        reference: `Carryforward from Year ${currentYear}`,
        batch: student?.batch || student?.batch_name,
        batch_id: batchId,
        roll_no: student?.roll_no,
        category: student?.category,
      };

      const res = await fetch(`${API_URL}/api/fees/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(cfPayload),
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        console.error("Carryforward API error:", res.status, errText);
        alert("Failed to create carryforward record. See console for details.");
        return;
      }

      await refreshData();
      setCurrentYear(nextYear);

      const cfDisplay = formatDateForDisplay(cfPaymentYMD);
      alert(`Carryforward of ${formatCurrency(cfAmount)} done to ${YEAR_LABELS[nextYear - 1]} (date set to ${cfDisplay}).`);
    } catch (err) {
      console.error("handleCarryForward error:", err);
      alert("Failed to carry forward. Check console for details.");
    }
  };

  const handlePrintVoucher = async (fee) => {
    try {
      const voucherNo = fee.receipt_no ? `RE${String(fee.receipt_no).match(/\d+/)?.[0] ?? fee.id}` : `RE${fee.id}`;
      await VoucherDownload.generatePDF({
        voucher_no: voucherNo,
        date: fee.payment_date,
        account_head: "Refund/Expense",
        reference: fee.reference,
        mode: fee.mode,
        amount: Math.abs(parseFloat(Number(fee.scholarship_amount || 0))),
        paid_to: fee.student_name || student?.name || "-",
        status: "Refunded",
        particulars: `Refund to ${fee.student_name || student?.name || "-"} for excess payment.`,
        remarks: `Refund processed for ${fee.student_name || student?.name || "-"} (${voucherNo})`,
        batch: fee.batch || fee.batch_name,
        class: fee.course_year
      });
    } catch (err) {
      console.error("Voucher generation failed:", err);
      alert("Failed to generate voucher PDF. Check console for details.");
    }
  };

  const handleDelete = async (fee_id) => {
    if (!window.confirm("Delete this fee record?")) return;
    try {
      const res = await fetch(`${API_URL}/api/fees/delete/${fee_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        console.error("Delete failed:", res.status);
        alert("Failed to delete fee record.");
        return;
      }
      await refreshData();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete fee. See console for details.");
    }
  };

  // IMPORTANT: This function uses the ReceiptDownload utility (blob+anchor) so it does NOT open a print window.
  // If the browser still opens the downloaded PDF in a new tab, that is a browser setting (auto-open PDFs).
  const handlePrintReceipt = async (fee) => {
    try {
      let originalPaymentDisplay = "-";
      const modeLower = String(fee.mode || "").toLowerCase();
      if (modeLower.includes("carryforward")) {
        const prevYear = Number(fee.course_year) - 1;
        if (Number.isFinite(prevYear) && prevYear >= 1) {
          const prevYearRecords = feeRecords.filter(r => Number(r.course_year) === prevYear);
          const triggerYMD = findTriggerDateForExcess(prevYearRecords, batchAnnualFee || 0);
          if (triggerYMD) originalPaymentDisplay = formatDateForDisplay(triggerYMD);
          else originalPaymentDisplay = formatDateForDisplay(fee.payment_date || new Date());
        } else {
          originalPaymentDisplay = formatDateForDisplay(fee.payment_date || new Date());
        }
      } else {
        originalPaymentDisplay = formatDateForDisplay(fee.payment_date || new Date());
      }

      await ReceiptDownload.generatePDF({
        ...fee,
        amount: parseFloat(Number(fee.amount || fee.amount_paid || 0)),
        student_name: student?.name || student?.student_name || "Student",
        category: student?.category || "N/A",
        course_year: YEAR_LABELS[(Number(fee.course_year) || currentYear) - 1],
        batch: student?.batch || student?.batch_name,
        roll_no: student?.roll_no || fee.roll_no,
        receipt_no: fee.receipt_no || fee.id || "-",
        original_payment_date: originalPaymentDisplay, // dd-mm-yyyy
      });
    } catch (err) {
      console.error("Receipt generation failed:", err);
      alert("Failed to generate receipt PDF. Check console for details.");
    }
  };

  if (error) return <div style={{ color: "red", padding: 20 }}>{error}</div>;
  if (loading || !student) return <div>Loading...</div>;

  return (
    <div className="fee-account-container">
      <button className="back-btn" onClick={() => navigate(-1)}>&larr; Back to Fee List</button>
      <h2 style={{ textAlign: "center" }}>Student Account: {student.name}</h2>
      <div className="student-account-info">
        <div><b>Roll No:</b> {student.roll_no}</div>
        <div><b>Category:</b> {student.category || "N/A"}</div>
        <div><b>Batch:</b> {student.batch || student.batch_name}</div>
      </div>
      <div className="feeaccount-tabs">
        {YEAR_VALUES.map((y, idx) => (
          <button
            key={y}
            className={`feeaccount-tab${currentYear === y ? " active" : ""}`}
            onClick={() => setCurrentYear(y)}
          >
            {YEAR_LABELS[idx]}
          </button>
        ))}
      </div>

      <div className="feeaccount-year-summary">
        <div><b>Class:</b> {YEAR_LABELS[currentYear - 1]}</div>
        {(batchAnnualFee === null || batchAnnualFee === undefined || Number(batchAnnualFee) === 0) ? (
          userRole === "admin" && batchId ? (
            <AnnualFeeInput
              batchId={batchId}
              token={token}
              onSet={amt => {
                setBatchAnnualFee(amt);
                refreshData();
              }}
            />
          ) : (
            <div style={{color:"red"}}>Annual Fee not set</div>
          )
        ) : (
          <div><b>Annual Fee:</b> {formatCurrency(batchAnnualFee)}</div>
        )}
        <div><b>Total Paid (Tuition):</b> {formatCurrency(totalPaid)}</div>
        <div><b>Total Scholarship:</b> {formatCurrency(totalScholarship)}</div>
        <div><b>Total Refund:</b> {formatCurrency(totalRefund)}</div>
        <div><b>Carryforward:</b> {formatCurrency(carryforwardAmount)}</div>
        <div><b>Total Received:</b> {formatCurrency(totalReceived)}</div>
        <div><b>Pending Amount:</b> <span style={{ color: "red" }}>
          {formatCurrency(pending)}
        </span></div>
        {canCarryForward && (
          <button className="fee-action-btn" style={{background:"#e67e22", marginTop: 10}} onClick={handleCarryForward}>
            Carry Forward Excess to Next Year
          </button>
        )}
        {canRefund && (
          <div style={{marginTop:10, color:"green"}}>Excess payment in third year. Please process refund.</div>
        )}
      </div>

      <div style={{display: "flex", gap: "10px", marginBottom: 12}}>
        <button className="fee-action-btn" onClick={() => setShowAddFee(true)}>+ Add Fee</button>
        <button
          className="fee-action-btn"
          style={{background:"#3478f6"}}
          onClick={() =>
            handlePrintTable(printRef, {
              classLabel: YEAR_LABELS[currentYear - 1],
              annualFee: formatCurrency(batchAnnualFee),
              totalPaid: formatCurrency(totalPaid),
              totalScholarship: formatCurrency(totalScholarship),
              totalRefund: formatCurrency(totalRefund),
              carryforward: formatCurrency(carryforwardAmount),
              totalReceived: formatCurrency(totalReceived),
              pending: formatCurrency(pending)
            })
          }
        >
          Print This Year
        </button>
        <button
          className="fee-action-btn"
          style={{background:"#3478f6"}}
          onClick={() => handlePrintTable(printAllRef)}
        >
          Print All Years
        </button>
      </div>

      {showAddFee && (
        <FeeEntryModal
          token={token}
          student={{ ...student, course_year: currentYear }}
          onClose={() => setShowAddFee(false)}
          onSave={() => {
            // onSave should refresh data only; do NOT open print windows here
            refreshData();
            // keep modal closed
            setShowAddFee(false);
          }}
          editMode={false}
          user={user}
        />
      )}
      {showEditFee && editFee && (
        <FeeEntryModal
          token={token}
          student={{ ...student, course_year: currentYear }}
          fee={editFee}
          editMode={true}
          onClose={() => { setShowEditFee(false); setEditFee(null); }}
          onSave={refreshData}
          user={user}
        />
      )}

      {/* Hidden print tables */}
      <div style={{ display: "none" }}>
        <PrintableFeeTable
          refProp={printRef}
          student={student}
          records={recordsForYear}
          classLabel={YEAR_LABELS[currentYear - 1]}
          batch={student?.batch || student?.batch_name}
        />
        <div ref={printAllRef}>
          <h2 style={{textAlign:"center"}}>Student Account: {student.name}</h2>
          <div style={{marginBottom:"12px"}}>
            <b>Roll No:</b> {student.roll_no} &nbsp;&nbsp;
            <b>Category:</b> {student.category || "N/A"} &nbsp;&nbsp;
            <b>Batch:</b> {student.batch || student.batch_name}
          </div>
          {YEAR_VALUES.map((y, idx) => {
            const yearRecords = getRecordsForYear(y);
            const yearTotalPaid = yearRecords.reduce(
              (sum, r) => r.mode !== "Carryforward from previous year" && r.status === "paid"
                ? sum + parseFloat(Number(r.amount_paid || r.amount || 0))
                : sum,
              0
            );
            const yearTotalScholarship = yearRecords.reduce((sum, r) => sum + (r.status && r.status.toLowerCase() === "refunded" ? 0 : parseFloat(Number(r.scholarship_amount || 0))), 0);
            const yearTotalRefund = yearRecords.reduce((sum, r) =>
              r.status && r.status.toLowerCase() === "refunded"
                ? sum + Math.abs(parseFloat(Number(r.scholarship_amount || 0)))
                : sum
            , 0);

            const yearCarryforwardAmount = getCarryforwardAmount(feeRecords, y);
            const nextYearCFRecords = getCarryforwardRecords(feeRecords, y + 1);

            const yearTotalReceived = yearTotalPaid + yearTotalScholarship + yearCarryforwardAmount - yearTotalRefund;
            const yearPending =
              (y === 1 || y === 2) && nextYearCFRecords.length
                ? 0
                : (batchAnnualFee || 0) - yearTotalReceived;

            return (
              <div key={y} style={{marginBottom: "40px"}}>
                <h3 style={{marginTop: 30, marginBottom: 5}}>{YEAR_LABELS[idx]}</h3>
                <div className="print-summary" style={{marginBottom:"8px"}}>
                  <span><b>Class:</b> {YEAR_LABELS[idx]}</span>
                  <span><b>Annual Fee:</b> {formatCurrency(batchAnnualFee)}</span>
                  <span><b>Total Paid (Tuition):</b> {formatCurrency(yearTotalPaid)}</span>
                  <span><b>Total Scholarship:</b> {formatCurrency(yearTotalScholarship)}</span>
                  <span><b>Total Refund:</b> {formatCurrency(yearTotalRefund)}</span>
                  <span><b>Carryforward:</b> {formatCurrency(yearCarryforwardAmount)}</span>
                  <span><b>Total Received:</b> {formatCurrency(yearTotalReceived)}</span>
                  <span><b>Pending Amount:</b> <span style={{ color: "red" }}>{formatCurrency(yearPending)}</span></span>
                </div>
                <PrintableFeeTable
                  student={student}
                  records={yearRecords}
                  classLabel={YEAR_LABELS[idx]}
                  batch={student?.batch || student?.batch_name}
                />
              </div>
            );
          })}
        </div>
      </div>

      <table className="fee-table">
        <thead>
          <tr>
            <th>Receipt No</th>
            <th>Batch</th>
            <th>Class</th>
            <th>Amount (₹)</th>
            <th>Status</th>
            <th>Payment Date</th>
            <th>Mode</th>
            <th>Transaction Details</th>
            <th>Scholarship (₹)</th>
            <th>Refund (₹)</th>
            {(userRole === "admin" || userRole === "principal") && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {recordsForYear.length === 0 ? (
            <tr>
              <td colSpan={(userRole === "admin" || userRole === "principal") ? 11 : 10} style={{ textAlign: "center" }}>No fee records found for this year.</td>
            </tr>
          ) : (
            recordsForYear.map((rec, idx) => (
              <tr key={rec.fee_id || rec.id || idx}>
                <td>{rec.receipt_no || rec.fee_id || rec.id || '-'}</td>
                <td>{student?.batch || student?.batch_name}</td>
                <td>{YEAR_LABELS[currentYear - 1]}</td>
                <td>{formatCurrency(parseFloat(Number(rec.amount || rec.amount_paid || 0)))}</td>
                <td>{rec.status}</td>
                <td>{rec.payment_date ? formatDateForDisplay(rec.payment_date) : ""}</td>
                <td>{rec.mode}</td>
                <td>{rec.reference}</td>
                <td>
                  {rec.status && rec.status.toLowerCase() === "refunded"
                    ? formatCurrency(0)
                    : formatCurrency(parseFloat(Number(rec.scholarship_amount || 0)))}
                </td>
                <td>
                  {rec.status && rec.status.toLowerCase() === "refunded"
                    ? formatCurrency(Math.abs(parseFloat(Number(rec.scholarship_amount || 0))))
                    : formatCurrency(0)}
                </td>

                {(userRole === "admin" || userRole === "principal") && (
                  <td>
                    {userRole === "admin" ? (
                      <>
                        <button className="fee-action-btn" style={{ background: "#184e77" }} onClick={() => { setEditFee(rec); setShowEditFee(true); }}>Edit</button>
                        <button className="fee-action-btn fee-delete" style={{ background: "#e63946", marginLeft: 6 }} onClick={() => handleDelete(rec.fee_id || rec.id)}>Delete</button>
                        {rec.status && rec.status.toLowerCase() === "refunded" ? (
                          <button className="fee-action-btn" style={{ background: "#4caf50", marginLeft: 6 }} onClick={() => handlePrintVoucher(rec)}>Voucher</button>
                        ) : (
                          <button className="fee-action-btn" style={{ background: "#3478f6", marginLeft: 6 }} onClick={() => handlePrintReceipt(rec)}>
                            {rec.mode === "Carryforward from previous year" ? "Receipt (Carryforward)" : "Receipt"}
                          </button>
                        )}
                      </>
                    ) : (
                      /* Principal: render Voucher for refunds, Receipt otherwise */
                      isRefundRow(rec) ? (
                        <button className="fee-action-btn" style={{ background: "#4caf50", color: "#fff" }} onClick={() => handlePrintVoucher(rec)}>Voucher</button>
                      ) : (
                        <button className="fee-action-btn" style={{ background: "#3478f6", color: "#fff" }} onClick={() => handlePrintReceipt(rec)}>
                          {rec.mode === "Carryforward from previous year" ? "Receipt (Carryforward)" : "Receipt"}
                        </button>
                      )
                    )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- PrintableFeeTable (display dd-mm-yyyy) ---------- */
const PrintableFeeTable = React.forwardRef(
  ({ student, records, classLabel, batch, refProp }, ref) => {
    const tableRef = ref || refProp;
    return (
      <div ref={tableRef}>
        <table className="printable-fee-table" style={{ width: "100%", borderCollapse: "collapse", marginBottom: 18 }}>
          <thead>
            <tr>
              <th>Receipt No</th>
              <th>Batch</th>
              <th>Class</th>
              <th>Amount (₹)</th>
              <th>Status</th>
              <th>Payment Date</th>
              <th>Mode</th>
              <th>Transaction Details</th>
              <th>Scholarship (₹)</th>
              <th>Refund (₹)</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: "center" }}>No fee records found for this year.</td>
              </tr>
            ) : (
              records.map((rec, idx) => (
                <tr key={rec.fee_id || rec.id || idx}>
                  <td>{rec.receipt_no || rec.fee_id || rec.id || '-'}</td>
                  <td>{batch}</td>
                  <td>{classLabel}</td>
                  <td>{formatCurrency(parseFloat(Number(rec.amount || rec.amount_paid || 0)))}</td>
                  <td>{rec.status}</td>
                  <td>{rec.payment_date ? formatDateForDisplay(rec.payment_date) : ""}</td>
                  <td>{rec.mode}</td>
                  <td>{rec.reference}</td>
                  <td>
                    {rec.status && rec.status.toLowerCase() === "refunded"
                      ? formatCurrency(0)
                      : formatCurrency(parseFloat(Number(rec.scholarship_amount || 0)))}
                  </td>
                  <td>
                    {rec.status && rec.status.toLowerCase() === "refunded"
                      ? formatCurrency(Math.abs(parseFloat(Number(rec.scholarship_amount || 0))))
                      : formatCurrency(0)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  }
);